/**
 * @file  src\pages\AgentCreateOrEditWantAdDraftListing.js
 * @brief  This file is responsible for agent want ad creation start page
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import SegmentDivider from "../components/MemberWantAd/SegmentDivider";
import MyDrafts from "../components/Common/MyDrafts";
import { useNavigate } from "react-router-dom";
import {
  AGENT_WANTAD_KEY,
  BUY,
} from "../constants/common";
import {
  getMyDraft,
  getWantAdById,
  saveMemberWantAd,
  getProfileSlug,
} from "../services/v2/wantAdService";
import { pageURLs } from "@/constants";
import {
  getUserType,
  loggedInUser,
} from "../helpers/common";
import BuyerList from "../images/buyer_listing_img.png";
import { useDispatch, useSelector } from "react-redux";
import PremiumFeatureIcon from "../images/v2/premium-features-stars.svg";
import CustomLinkModal from "@/components/Common/CustomLinkModal";
import PremiumPlanModal from "../components/PlanUpgrade/PremiumPlanModal";
import { fetchUserDetails } from "../actions/userprofile";
import { PageWrapper } from "@/components/PageWrapper";
import useAgentGitchaRestrictions from "@/hooks/useAgentGitchaRestrictions";

const AgentCreateOrEditWantAdDraftListing = (props) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [myDrafts, setMyDrafts] = useState(null);
  const [userSlug, setUserSlug] = useState("");
  const userType = getUserType();

  const { shouldRestrictAgent } = useAgentGitchaRestrictions();

  const userDetailsFromStore = useSelector(
    (state) => state.userProfile.userDetails,
  );

  const [isPremiumUser, setIsPremiumUser] = useState(null);
  const agent = loggedInUser();

  // Custom link management states
  const [linkToShare, setLinkToShare] = useState("");
  const [showCustomLinkPopuop, setShowCustomLinkPopup] = useState(false);

  const [selectedOption, setSelectedOption] = useState(null);

  // control handlers for custom link popup
  const handleCustomLinkPopupOpen = () => {
    setShowCustomLinkPopup(true);
  };

  const handleCustomLinkPopupClose = () => {
    setShowCustomLinkPopup(false);
    setSelectedOption(null);
  };

  //Plan modal for non-pro agent
  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const [reload, setReload] = useState(0);

  const updateReloadParam = () => {
    setReload(reload + 1);
  };

  useEffect(() => {
    if (userDetailsFromStore !== null && userDetailsFromStore?.length > 0) {
      if (userDetailsFromStore[0]?.period !== 1 && userType === 2) {
        setIsPremiumUser(true);
      } else {
        setIsPremiumUser(false);
      }
    }
  }, [userDetailsFromStore]);

  useEffect(() => {
    //Fetch user details again if membership plan is upgraded
    dispatch(fetchUserDetails());
  }, [reload]);

  useEffect(() => {
    localStorage.removeItem(AGENT_WANTAD_KEY);
    if (isPremiumUser != null) {
      getMyDraft()
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (responseResult && responseResult.length !== 0) {
            setMyDrafts(responseResult);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [isPremiumUser]);
  /**
   * To handle navigation to edit want ad on clicking draft want ad
   * @param {*} requestPayLoad
   */
  const handleDraftItemClick = async (requestPayLoad) => {
    let wantAdId = requestPayLoad.selectedId;
    if (wantAdId) {
      await getWantAdById(wantAdId)
        .then((response) => {
          const responseResult = _.get(response, "result", null);
          if (response.errorCode === 0 && responseResult) {
            localStorage.setItem(
              AGENT_WANTAD_KEY,
              JSON.stringify(responseResult),
            );
            navigate(`${pageURLs.editWantAdForAgents}/${responseResult.wantAdId}`);
          } else {
            console.log("Something went wrong");
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  };
  /**
   * To load draft want ads
   * @returns
   */

  const hasDrafts = myDrafts?.length > 0;

  const loadDrafts = () => {
    if (myDrafts) {
      const myDraftList = myDrafts?.map((md) => {
        const wantAdId = _.get(md, "Want_ad_Id");
        const CompletedPercentage = _.get(md, "CompletedPercentage", "");
        const adTypeId = _.get(md, "Buy_or_Rent", "");
        const Current_Step = _.get(md, "Current_Step", "");
        const IsReadyToReview = _.get(md, "IsReadyToReview", "");
        const IsFilledByClient = _.get(md, "IsFilledByClient", "");
        let lastUpdatedDate = new Date(_.get(md, "LastUpdatedTS", ""));
        let formattedDate = [
          ("0" + (lastUpdatedDate.getMonth() + 1)).slice(-2),
          ("0" + lastUpdatedDate.getDate()).slice(-2),
          lastUpdatedDate.getFullYear(),
        ].join("/");
        let adType = "";
        if (adTypeId === 1) {
          adType = "Buy";
        } else if (adTypeId === 2) {
          adType = "Rent (Long-term)";
        } else if (adTypeId === 3) {
          adType = "Rent (Short-term)";
        }
        let clientName;
        if (_.get(md, "ClientFirstName") && _.get(md, "ClientLastName")) {
          clientName =
            _.get(md, "ClientFirstName") + " " + _.get(md, "ClientLastName");
        } else if (_.get(md, "ClientFirstName")) {
          clientName = _.get(md, "ClientFirstName");
        }

        return (
          <MyDrafts
            key={wantAdId}
            itemId={wantAdId}
            CompletedPercentage={CompletedPercentage}
            adType={adType}
            Current_Step={Current_Step}
            setDraftSelectedItem={handleDraftItemClick}
            latUpdatedDate={formattedDate}
            clientName={clientName}
            isFromWantAd={true}
            IsFilledByClient={IsFilledByClient}
            IsReadyToReview={IsReadyToReview}
          />
        );
      });

      return myDraftList;
    }
  };

  useEffect(() => {
    getProfileSlugApi();
  }, []);

  const getProfileSlugApi = async () => {
    await getProfileSlug()
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        setUserSlug(responseResult);
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  // Function to save want ad and generate custom link for buyer form
  const generateWantAdCustomLink = () => {
    // If slug is not ready, wait 2 seconds and try again.
    if (!userSlug) {
      setTimeout(() => {
        if (!userSlug) {
          alert(
            "Profile information is still loading. Please try again later.",
          );
        } else {
          processGenerateLink();
        }
      }, 2000);
      return;
    }

    processGenerateLink();
  };

  const processGenerateLink = () => {
    const requestPayLoad = {
      isFilledByClient: true,
      isAgentAsSeller: false,
      IsSavedDraft: true,
      IsPublished: false,
      cmpldStep: 1,
      cmpldPerctg: 7,
      adType: BUY,
      isAgentCreated: true,
    };

    saveMemberWantAd(requestPayLoad)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        if (response.errorCode === 0 && responseResult) {
          setLinkToShare(
            process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL +
            "/form/" +
            userSlug,
          );
          handleCustomLinkPopupOpen();
        } else {
          console.log("Something went wrong");
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  };

  const PremiumPlanModalClose = () => {
    setPremiumPlanModal(false);
    setSelectedOption(null);
  };
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const handleBuyerListingFormSelect = () => {
    setSelectedOption("buyerListingForm");
    if (isPremiumUser) generateWantAdCustomLink();
    else PremiumPlanModalShow();
  };

  const handleCreateNewSelect = () => {
    setSelectedOption("newWantListing");
    navigate(pageURLs.agentCreatewantad);
  };

  return (
    <>
      <CustomLinkModal
        show={showCustomLinkPopuop}
        onHide={handleCustomLinkPopupClose}
        linkToShare={linkToShare}
        isFromCreateWantAd={true}
      />

      {/* Mounted only while open: a second always-mounted copy left a stray
          static backdrop swallowing every click on the options below. */}
      {showPremiumPlanModal && (
        <PremiumPlanModal
          userDetails={userDetailsFromStore[0]}
          show={showPremiumPlanModal}
          onHide={PremiumPlanModalClose}
          updateReloadParam={updateReloadParam}
          showPlanModal={PremiumPlanModalShow}
        />
      )}

      <PageWrapper>
        <div className="m-0 pb-3">
          <div className="main-content p-0 agent-no-login-content">
            {/* To do when admin login is true when we use (m-2) */}
            {/* <div className="row m-2"> */}
            <div className="row m-0">
              <div className="col-md-12">
                <div className="edit-want-ad-wrapper mw-640">
                  <h2 className="agent-create-head">Create a Want-Listing</h2>
                  <h6 className="mb-40 agent-create-para">
                    Select how you would you like to create your Want-Listing.{" "}
                  </h6>

                  {!shouldRestrictAgent && (
                    <>
                      <div className="create-new-wantad-wrap mb-3">
                        <label className="radio-wrap">
                          <input
                            name="SELL4"
                            type="radio"
                            value="buyerListingForm"
                            checked={selectedOption === "buyerListingForm"}
                            onChange={handleBuyerListingFormSelect}
                          />
                          <span>
                            <div className="timing-wrap-info">
                              <h2 className="pb-0 timing-wrap-head">
                                Send a Buyer Listing Form to my client
                              </h2>
                              {!isPremiumUser && (
                                <img
                                  src={PremiumFeatureIcon}
                                  alt={PremiumFeatureIcon}
                                  className=""
                                />
                              )}
                            </div>
                          </span>
                        </label>
                      </div>

                      <div className="text-center">
                        <div className="mt-3 mb-3">
                          <img src={BuyerList} className="wdImg" />
                        </div>
                        <div className="wdImg m-auto mb-3 boldTxt">
                          {" "}
                          <p className="wd-img-para">
                            After your buyer submits their criteria to you via
                            your custom link, we notify you to review and post it
                            from your drafts. (Buy only)
                          </p>
                        </div>
                      </div>

                      <SegmentDivider />
                    </>
                  )}

                  <>
                    <div className="my-drafts-sec">
                      <div className="create-new-wantad-wrap mt-4 mb-4">
                        <label className="radio-wrap">
                          <input
                            name="SELL4"
                            type="radio"
                            value="newWantListing"
                            checked={selectedOption === "newWantListing"}
                            onChange={handleCreateNewSelect}
                          />
                          <span>
                            <div className="timing-wrap-info">
                              <h2 className="pb-0 timing-wrap-head">
                                Create New Want-Listing
                              </h2>
                            </div>
                          </span>
                        </label>
                      </div>
                      {(myDrafts || isPremiumUser === true) && hasDrafts && (
                        <>
                          <h2 className="mt-3">My Drafts</h2>
                          {loadDrafts()}
                        </>
                      )}
                    </div>
                  </>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PageWrapper>
    </>
  );
};

export default AgentCreateOrEditWantAdDraftListing;
